import * as React from 'react';
import { cn } from '@/lib/utils';
import InfoIcon from '@/icons/Info.svg?react';
import ChevronDownIcon from '@/icons/ChevronDown.svg?react';
import ChevronUpIcon from '@/icons/ChevronUp.svg?react';
import { typography } from '@/styles/typography';

export interface WarningBannerContent {
  /** Text displayed when not collapsible, or the full text when collapsible and expanded */
  text?: string;
  /** Short text shown when collapsed (only used when collapsible: true) */
  collapsedText?: string;
  /** Full text shown when expanded (only used when collapsible: true) */
  expandedText?: string;
  /** Whether the banner can be collapsed/expanded. Default: false */
  collapsible?: boolean;
}

export interface WarningBannerProps {
  content: WarningBannerContent;
  /** Controlled expanded state (optional -- uncontrolled by default) */
  isExpanded?: boolean;
  /** Callback when expand/collapse is toggled */
  onToggle?: (expanded: boolean) => void;
  className?: string;
}

/**
 * WarningBanner component - displays a warning/info banner with optional collapsible mode.
 *
 * Non-collapsible mode renders a static warning banner with an info icon and text.
 * Collapsible mode shows collapsed text by default with a chevron toggle to expand
 * and reveal the full expanded text.
 *
 * @example
 * ```tsx
 * // Non-collapsible (ResourceCatalog)
 * <WarningBanner
 *   content={{ text: "If you close this website, the resource catalog will not be saved." }}
 * />
 *
 * // Collapsible (Events disclaimer)
 * <WarningBanner
 *   content={{
 *     collapsedText: "This calendar is provided as an informational resource...",
 *     expandedText: "This calendar is provided as an informational resource to consolidate...",
 *     collapsible: true,
 *   }}
 * />
 * ```
 */
export function WarningBanner({
  content,
  isExpanded: controlledExpanded,
  onToggle,
  className,
}: WarningBannerProps) {
  const [internalExpanded, setInternalExpanded] = React.useState(false);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const nextExpanded = !expanded;
    if (!isControlled) {
      setInternalExpanded(nextExpanded);
    }
    onToggle?.(nextExpanded);
  };

  if (!content) return null;

  const isCollapsible = content.collapsible === true;

  // Determine the text to display
  const displayText = isCollapsible
    ? expanded
      ? content.expandedText
      : content.collapsedText
    : content.text;

  if (!displayText) return null;

  return (
    <div
      className={cn('w-full', className)}
      style={{
        borderRadius: '4px',
        border: '1px solid #F3DF89',
        padding: '16px 24px',
        background: '#F3DF89',
        display: 'flex',
        alignItems: 'flex-start',
      }}
      role="status"
      aria-live="polite"
    >
      <InfoIcon
        style={{ width: '32px', height: '32px', flexShrink: 0, marginRight: '12px', marginTop: '2px' }}
        aria-hidden="true"
      />
      <p
        className={cn(typography.bodyMedium, 'text-black m-0')}
        style={{ flex: 1, marginTop: '4px' }}
      >
        {displayText}
      </p>
      {isCollapsible && (
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse disclaimer' : 'Expand disclaimer'}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '16px',
            marginTop: '6px',
          }}
        >
          {expanded ? (
            <ChevronUpIcon
              style={{ width: '16px', height: '16px' }}
              aria-hidden="true"
            />
          ) : (
            <ChevronDownIcon
              style={{ width: '16px', height: '16px' }}
              aria-hidden="true"
            />
          )}
        </button>
      )}
    </div>
  );
}

WarningBanner.displayName = 'WarningBanner';

export default WarningBanner;
